import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Route, Redirect } from 'react-router-dom';
import { useLocation, useHistory } from 'react-router-dom';

import Providers from 'providers';

import { LOCAL_STORAGE } from 'utils/constants';
import ROUTES from 'utils/routes';
import { getTokenRequest, refreshTokenRequest, signOut, INACTIVE_TIMEOUT, getRoleToken } from 'utils/auth';

import Layout from 'components/Layout';
import ViewSignOut from 'components/ViewSignOut';
import SessionTimeoutDetector from 'components/SessionTimeoutDetector';

const PrivateRoute = ({ component: Component, ...rest }) => {
  const [isLoggedOut, setIsLoggedOut] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const location = useLocation();
  const history = useHistory();
  const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);

  const [, code] = location.search.split('?code=');

  useEffect(() => {
    async function getToken() {
      if (location.search.includes('code')) {
        try {
          const { idToken, refreshToken } = await getTokenRequest({ code });
          localStorage.setItem(LOCAL_STORAGE.TOKEN, idToken);
          localStorage.setItem(LOCAL_STORAGE.REFRESH_TOKEN, refreshToken);
          const role = await getRoleToken();
          localStorage.setItem(LOCAL_STORAGE.ROLE_ID, role);
          history.replace(`/`);
        } catch (error) {
          setIsLoggedOut(true);
        }
      }
    }

    getToken();
  }, [history, location.search, code]);

  useEffect(() => {
    if (token && !isLoggedOut) {
      setPermissions([]);
    }
  }, [isLoggedOut, token]);

  const refreshToken = async () => {
    const { idToken } = await refreshTokenRequest(localStorage.getItem(LOCAL_STORAGE.REFRESH_TOKEN));
    localStorage.setItem(LOCAL_STORAGE.TOKEN, idToken);
  };

  if (token) {
    return isLoggedOut ? (
      <ViewSignOut noPermissions={!permissions?.length} inactiveTimeout={INACTIVE_TIMEOUT} onSetInactive={signOut} />
    ) : (
      <Route
        {...rest}
        render={props => (
          <Providers features={permissions}>
            <Layout>
              <Component {...props} />
              <SessionTimeoutDetector onIdleSessionExpired={() => setIsLoggedOut(true)} onContinue={refreshToken} />
            </Layout>
          </Providers>
        )}
      />
    );
  } else {
    return location.search.includes('code') ? null : (
      <Route {...rest} render={() => <Redirect to={ROUTES.SIGN_IN} />} />
    );
  }
};

PrivateRoute.propTypes = {
  component: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
};

export default PrivateRoute;
